import { useState, useCallback, useEffect } from 'react';
import type { Word } from '../../types';
import { api } from '../../services/api';
import ApproveButton from '../ApproveButton';
import MiniCalendar from '../MiniCalendar';
import FieldCopyButton from '../FieldCopyButton';
import { resolveSelectedText } from '../../utils/resolveSelected';
import './StageCommon.css';
import './FinalReviewStage.css';

/**
 * Stage 10: Final Review & Schedule.
 * Summary of all approved content + date picker for game_date.
 * Read-only overview with links back to each stage for editing.
 */

interface FinalReviewStageProps {
  word: Word;
  onApprove: (fields: Record<string, unknown>) => void;
  onNavigate?: (stageId: string) => void;
  saving: boolean;
  registerApprovePayload: (fn: (() => Record<string, unknown> | null) | null) => void;
}

interface ScheduledWord {
  word: string;
  date: string;
  id: string;
  difficulty?: string;
}

interface ReplaceConfirm {
  date: string;
  existing: ScheduledWord;
}

interface SummarySection {
  title: string;
  stage: string;
  fields: string[];
}

const SUMMARY_SECTIONS: SummarySection[] = [
  {
    title: 'Core Identity',
    stage: 'stage1',
    fields: ['definition_enhanced', 'etymology', 'usage_context', 'pronunciation_tip', 'difficulty', 'word_themes', 'word_vibe'],
  },
  {
    title: 'Fake Definitions',
    stage: 'stage1b',
    fields: ['selected_wrong_1', 'selected_wrong_2', 'selected_wrong_3'],
  },
  {
    title: 'Example Sentences',
    stage: 'stage2',
    fields: ['selected_example_1', 'selected_example_2', 'selected_example_3'],
  },
  {
    title: 'How To Use',
    stage: 'stage3',
    fields: ['selected_how_to_use_1', 'selected_how_to_use_2', 'selected_how_to_use_3'],
  },
  {
    title: 'Fun Facts',
    stage: 'stage4',
    fields: ['selected_fun_fact_1', 'selected_fun_fact_2', 'selected_fun_fact_3'],
  },
  {
    title: 'Audio',
    stage: 'stage5',
    fields: ['audio_url'],
  },
  {
    title: 'Reframes',
    stage: 'stage6',
    fields: ['selected_newsletter_reframe', 'selected_second_chance_reframe', 'selected_third_chance_reframe'],
  },
  {
    title: 'NLP & SEO',
    stage: 'stage1',
    fields: ['winner_reframe', 'loser_reframe', 'share_card_benefit_hook', 'paa_q1', 'paa_a1'],
  },
];

export default function FinalReviewStage({ word, onApprove, onNavigate, saving, registerApprovePayload }: FinalReviewStageProps) {
  const [gameDate, setGameDate] = useState<string>((word.game_date as string) || '');
  const [scheduledWords, setScheduledWords] = useState<ScheduledWord[] | null>(null);
  const [loadingCalendar, setLoadingCalendar] = useState<boolean>(false);
  const [replaceConfirm, setReplaceConfirm] = useState<ReplaceConfirm | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCalendar(true);
      try {
        const result = await api.get('/api/words') as { words?: Record<string, unknown>[] };
        if (!cancelled) {
          const scheduled: ScheduledWord[] = (result.words || [])
            .filter((w: Record<string, unknown>) => w.game_date && w.id !== word.id)
            .map((w: Record<string, unknown>) => ({ word: w.word as string, date: w.game_date as string, id: w.id as string, difficulty: w.difficulty as string | undefined }));
          setScheduledWords(scheduled);
        }
      } catch {
        if (!cancelled) setScheduledWords([]);
      } finally {
        if (!cancelled) setLoadingCalendar(false);
      }
    })();
    return () => { cancelled = true; };
  }, [word.id]);

  useEffect(() => {
    if (!registerApprovePayload) return;
    registerApprovePayload(() =>
      gameDate ? { game_date: gameDate, send_to_games_core: true } : null
    );
  }, [registerApprovePayload, gameDate]);

  const handleCalendarSelect = useCallback((dateStr: string, existingWord?: ScheduledWord | null) => {
    if (existingWord) {
      setReplaceConfirm({ date: dateStr, existing: existingWord });
    } else {
      setGameDate(dateStr);
      setReplaceConfirm(null);
    }
  }, []);

  const handleConfirmReplace = useCallback(() => {
    if (replaceConfirm) {
      setGameDate(replaceConfirm.date);
      setReplaceConfirm(null);
    }
  }, [replaceConfirm]);

  const handleSchedule = useCallback(() => {
    if (!gameDate) return;
    onApprove({
      game_date: gameDate,
      send_to_games_core: true,
    });
  }, [gameDate, onApprove]);

  return (
    <div className="stage-view">
      <div className="final-review">
        <h2 className="final-review-title">Final Review</h2>
        <p className="final-review-subtitle">
          Review all approved content for <strong>{word.word}</strong>, then schedule a game date.
        </p>

        {/* Summary sections */}
        <div className="final-sections">
          {SUMMARY_SECTIONS.map((section) => {
            const filledFields = section.fields.filter((f) => word[f]);
            if (filledFields.length === 0) return null;

            return (
              <div key={section.title} className="final-section">
                <div className="final-section-header">
                  <h3 className="final-section-title">{section.title}</h3>
                  {onNavigate && (
                    <button
                      className="final-edit-link"
                      onClick={() => onNavigate(section.stage)}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <div className="final-section-fields">
                  {filledFields.map((field) => {
                    const text = field === 'audio_url' ? '' : (resolveSelectedText(field, word) ?? String(word[field] || ''));
                    return (
                      <div key={field} className="final-field field-copy-host">
                        <span className="final-field-label">
                          {formatLabel(field)}
                          {field !== 'audio_url' && <FieldCopyButton text={text} label={`Copy ${formatLabel(field)}`} />}
                        </span>
                        <span className="final-field-value">
                          {field === 'audio_url' ? (
                            <audio src={word[field] as string} controls className="final-audio" />
                          ) : (
                            text
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Schedule section */}
        <div className="final-schedule">
          <h3 className="final-schedule-title">Schedule Game Date</h3>

          <div className="final-date-row">
            <input
              type="date"
              className="final-date-input"
              value={gameDate}
              onChange={(e) => setGameDate(e.target.value)}
            />
            {gameDate && (
              <span className="final-date-display">
                {new Date(gameDate + 'T12:00:00').toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            )}
          </div>

          {replaceConfirm && (
            <div className="final-replace-confirm">
              <span>{replaceConfirm.existing.word} is already scheduled for this date. Replace it?</span>
              <div className="final-replace-actions">
                <button className="final-replace-btn" onClick={handleConfirmReplace}>Replace</button>
                <button className="final-replace-cancel" onClick={() => setReplaceConfirm(null)}>Cancel</button>
              </div>
            </div>
          )}

          {loadingCalendar ? (
            <span className="final-loading-cal">Loading calendar...</span>
          ) : scheduledWords && (
            <MiniCalendar
              scheduledWords={scheduledWords}
              selectedDate={gameDate}
              onSelectDate={handleCalendarSelect}
              currentWord={word.word}
            />
          )}
        </div>

        {/* Schedule & approve */}
        <ApproveButton
          onClick={handleSchedule}
          disabled={!gameDate || saving}
          loading={saving}
          label="Schedule & Save"
        />
      </div>
    </div>
  );
}

function formatLabel(fieldName: string): string {
  return fieldName
    .replace(/^selected_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
